import { ReactNode, useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router'
import { ProjectSelector } from '@/components/ProjectSelector'
import { useShowcaseAccess } from '@/showcase/access'
import { SHOWCASE_ENABLED } from '@/config/showcase'
import { usePollsAccess } from '@/config/polls'
import { Topbar } from './Topbar'

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  counter?: number
  group?: string
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/dashboard',
    label: 'Дашборд',
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" /></svg>
    ),
  },
  {
    to: '/analytics',
    label: 'Аналитика',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" /></svg>
    ),
  },
  {
    to: '/billing',
    label: 'Биллинг',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18M8 3v6" /></svg>
    ),
  },
]

// Доп. пункт «Витрина» для whitelist'а (Phase 1 rollout — только основателю).
// Спрятан за useShowcaseAccess(), для обычных юзеров не появится.
const SHOWCASE_NAV_ITEM: NavItem = {
  to: '/showcase',
  label: 'Витрина',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
  ),
}

// Доп. пункт «Опросы» для whitelist'а (Phase 1 — только 8931841, ТЗ v3.1a §7.2).
// Скрыт за usePollsAccess(). Lab-membership overlay внутри страницы —
// вторая, ортогональная защита (§2.0 / §7.4.1).
const POLLS_NAV_ITEM: NavItem = {
  to: '/polls',
  label: 'Опросы',
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h4l3 8 4-16 3 8h4" /></svg>
  ),
}

export function DesktopShell({ children }: { children: ReactNode }) {
  const showcaseAccess = useShowcaseAccess()
  const pollsAccess = usePollsAccess()

  // Группировка + опциональная вставка «Витрины» и «Опросов» перед «Биллингом» для whitelist'а.
  // Порядок: Дашборд · Аналитика · [Витрина] · [Опросы] · Биллинг.
  const items: NavItem[] = [
    NAV_ITEMS[0], // Дашборд
    NAV_ITEMS[1], // Аналитика
    ...(SHOWCASE_ENABLED && showcaseAccess ? [SHOWCASE_NAV_ITEM] : []),
    ...(pollsAccess ? [POLLS_NAV_ITEM] : []),
    NAV_ITEMS[2], // Биллинг
  ]

  const mainItems = items.filter((i) => !i.group)
  const groupedItems = items.filter((i) => i.group)
  const groups = Array.from(new Set(groupedItems.map((i) => i.group!)))

  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  // Закрываем мобильный sidebar при навигации
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  return (
    <div className={`app ${mobileOpen ? 'sidebar-open' : ''}`}>
      <button className="burger" onClick={() => setMobileOpen((v) => !v)} aria-label="Меню">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {mobileOpen ? <path d="M18 6L6 18M6 6l12 12" /> : <path d="M3 6h18M3 12h18M3 18h18" />}
        </svg>
      </button>
      {mobileOpen && <div className="sidebar-veil" onClick={() => setMobileOpen(false)} />}
      <aside className="sidebar">
        <a href="/dashboard" className="brand" aria-label="TeleGraphyx">
          <img src="/assets/tgx-logo.svg" alt="TeleGraphyx" className="brand-logo-full" />
        </a>

        {mainItems.map((item) => (
          <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
            {item.icon}
            <span>{item.label}</span>
            {item.counter !== undefined && <span className="nav-counter">{item.counter}</span>}
          </NavLink>
        ))}

        {groups.map((group) => (
          <div key={group}>
            <div className="nav-section">{group}</div>
            {groupedItems
              .filter((i) => i.group === group)
              .map((item) => (
                <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                  {item.icon}
                  <span>{item.label}</span>
                  {item.counter !== undefined && <span className="nav-counter">{item.counter}</span>}
                </NavLink>
              ))}
          </div>
        ))}

        <div className="sidebar-spacer" />

        <ProjectSelector />
      </aside>

      <div className="main">
        <Topbar />
        <main className="content">{children}</main>
      </div>

      <style>{`
        .app {
          display: grid;
          grid-template-columns: var(--tg-sidebar-w, 240px) 1fr;
          min-height: 100vh;
        }
        .sidebar {
          background: var(--tg-surface, #fff);
          border-right: 1px solid var(--tg-line-2, #f0f0f4);
          padding: 20px 14px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          position: sticky;
          top: 0;
          height: 100vh;
        }
        .brand {
          display: flex;
          align-items: center;
          padding: 4px 6px 18px;
          border-bottom: 1px solid var(--tg-line, #e8e8ec);
          margin-bottom: 12px;
        }
        .brand-logo-full {
          height: 28px;
          width: auto;
        }
        .nav-section {
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--tg-mute, #888);
          padding: 12px 12px 6px;
        }
        .nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px 12px;
          border-radius: 8px;
          font-weight: 500;
          font-size: 14px;
          color: var(--tg-ink-2, #555);
          cursor: pointer;
          position: relative;
          transition: background 150ms ease-out;
        }
        .nav-link:hover {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .nav-link svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
          opacity: 0.7;
        }
        .nav-link.active {
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0052cc);
          font-weight: 600;
        }
        .nav-link.active svg {
          opacity: 1;
        }
        .nav-link.active::before {
          content: '';
          position: absolute;
          left: -14px;
          top: 6px;
          bottom: 6px;
          width: 3px;
          border-radius: 0 3px 3px 0;
          background: var(--tg-blue, #0066ff);
        }
        .nav-counter {
          margin-left: auto;
          font-size: 11px;
          font-weight: 600;
          color: var(--tg-mute, #888);
          background: var(--tg-surface-2, #f5f5f9);
          padding: 2px 7px;
          border-radius: 999px;
        }
        .nav-link.active .nav-counter {
          background: white;
          color: var(--tg-blue-press, #0052cc);
        }
        .sidebar-spacer {
          flex: 1;
        }
        .project-switcher {
          padding: 12px;
          background: var(--tg-surface-2, #f5f5f9);
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .project-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #FF8C42, #FF4000);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 11px;
          text-transform: uppercase;
        }
        .project-meta { flex: 1; min-width: 0; }
        .project-name {
          font-size: 13px;
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .project-sub {
          font-size: 11px;
          color: var(--tg-mute, #888);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .main {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .content {
          max-width: var(--tg-content-max, 1280px);
          margin: 0 auto;
          padding: 32px var(--tg-content-pad, 32px) 80px;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .burger {
          display: none;
          position: fixed;
          top: 14px; left: 14px;
          z-index: 110;
          width: 38px; height: 38px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2);
          border-radius: 10px;
          align-items: center; justify-content: center;
          cursor: pointer;
          color: var(--tg-ink, #111);
          box-shadow: 0 4px 12px -4px rgba(0,0,0,0.1);
        }
        .burger svg { width: 18px; height: 18px; }
        .sidebar-veil {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(38,40,45,0.4);
          z-index: 90;
        }
        @media (max-width: 900px) {
          .app { grid-template-columns: 1fr; }
          .burger { display: flex; }
          .sidebar {
            position: fixed;
            top: 0; left: 0;
            width: 260px;
            height: 100vh;
            z-index: 100;
            transform: translateX(-100%);
            transition: transform 200ms ease-out;
            box-shadow: 4px 0 20px -8px rgba(0,0,0,0.2);
          }
          .app.sidebar-open .sidebar { transform: translateX(0); }
          .app.sidebar-open .sidebar-veil { display: block; }
          /* Когда sidebar открыт — крест уезжает на правый край сайдбара чтоб не закрывать логотип */
          .app.sidebar-open .burger {
            left: calc(260px - 38px - 14px);
          }
          .content { padding-top: 64px; }
        }
      `}</style>
    </div>
  )
}
